import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import API_BASE_URL from "../config/api";

export default function StudentCategory() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // ✅ GET USER
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.id;

  /* ---------------------------------------------------
     IF NOT LOGGED IN → REDIRECT TO LOGIN
  --------------------------------------------------- */
  useEffect(() => {
    if (!userId) {
      navigate("/login");
    }
  }, [userId, navigate]);

  /* ---------------------------------------------------
     FETCH SUBJECT CATEGORIES (ONLY WHEN LOGGED IN)
  --------------------------------------------------- */
  useEffect(() => {
    if (!userId) return;

    const fetchCategories = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/active_book_categories.php?user_id=${userId}`
        );

        if (!res.ok) throw new Error("API failed");

        const data = await res.json();

        if (data?.success && Array.isArray(data.categories)) {
          const formatted = data.categories.map((cat) => ({
            id: cat.category_id,
            title: cat.category_name || "Untitled Subject",
            image:
              cat.picture_url && cat.picture_url.trim() !== ""
                ? cat.picture_url
                : `${process.env.PUBLIC_URL}/assets/img/subject-placeholder.jpg`,
            isActive: Boolean(cat.isActive),
            daysLeft: Number(cat.daysLeft ?? 0),
            total_quizzes: Number(cat.total_quizzes ?? 0),
          }));

          setCategories(formatted);
        } else {
          setCategories([]);
          setError("No subjects found");
        }
      } catch (err) {
        console.error("Subject fetch error:", err);
        setError("Failed to load subjects");
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [userId]);

  /* ---------------------------------------------------
     HANDLE CARD CLICK
  --------------------------------------------------- */
  const handleCardClick = async (cat) => {

    // Plan check
    if (!cat.isActive || cat.daysLeft <= 0) {
      navigate("/subscription");
      return;
    }

    // Quiz check
    if (cat.total_quizzes <= 0) {
      Swal.fire({
        icon: "info",
        title: "Quiz Not Available",
        text: "No quiz is available for this subject.",
        confirmButtonColor: "#164d85"
      });
      return;
    }

    const result = await Swal.fire({
      title: "Select Test Type",
      text: "Choose which type of mock test you'd like to start.",
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: "Chapter-wise Test",
      cancelButtonText: "Master Test",
      confirmButtonColor: "#00a65a",
      cancelButtonColor: "#1c5592",
      allowOutsideClick: false
    });

    if (result.isConfirmed) {
      navigate(`/mocktest/chapterwise/${cat.id}`);
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      navigate(`/mocktest/masterquiz/${cat.id}`);
    }
  };

  /* ---------------------------------------------------
     UI
  --------------------------------------------------- */
  if (!userId) return null;

  return (
    <section className="pt-2 pb-5 bg-light">
      <div className="container">
        {loading && (
          <div className="text-center py-5 text-muted fw-semibold">
            Loading subjects...
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-warning text-center">{error}</div>
        )}

        <div className="row gx-3 gy-3">
          {categories.map((cat) => (
            <div key={cat.id} className="col-6 col-xl-4 col-lg-4 col-md-4">
              <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                {/* IMAGE */}
                <div style={{ position: "relative" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}${cat.image}`}
                    alt={cat.title}
                    className="w-100"
                  />

                  {/* STATUS */}
                  <div
                    style={{
                      bottom: 0,
                      width: "100%",
                      padding: "4px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#fff",
                      textAlign: "center",
                      background:
                        cat.isActive && cat.daysLeft > 0
                          ? "linear-gradient(90deg,#28a745,#5dd39e)"
                          : "linear-gradient(90deg,#ff5f6d,#ff8a8a)",
                    }}
                  >
                    {cat.isActive && cat.daysLeft > 0
                      ? `${cat.daysLeft} Days Left`
                      : "Plan Not Active"}
                  </div>
                </div>

                {/* BODY */}
                <div className="card-body text-center d-flex flex-column p-3">
                  {/*<h5 className="fw-semibold mb-3">{cat.title}</h5>*/}

                  <button
                    className={`btn w-100 fw-semibold ${cat.isActive && cat.daysLeft > 0
                      ? "btn-primary"
                      : "btn-outline-danger"
                      }`}
                    onClick={() => handleCardClick(cat)}
                  >
                    {cat.isActive && cat.daysLeft > 0
                      ? "Start Now"
                      : "Subscribe Now"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
